// icon-map.ts
// Maps Bootstrap Icons class names (without the 'bi-' prefix) to Lucide icon data arrays.
// Used by IconComponent to resolve the correct SVG without importing all icons.
//
// Lucide icons reference: https://lucide.dev/icons/
// This file covers every bi-* class found in the app (138 entries as of Phase 1 audit).

import {
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  ArrowUp,
  Award,
  Banknote,
  Bell,
  Bike,
  BookOpen,
  BookmarkCheck,
  Building,
  Calendar,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarHeart,
  CalendarPlus,
  CalendarRange,
  CalendarX,
  Camera,
  ChartBar,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsLeft,
  ChevronsRight,
  Circle,
  CircleAlert,
  CircleCheck,
  CirclePause,
  CirclePlay,
  CirclePlus,
  CircleQuestionMark,
  CircleSlash,
  CircleUser,
  CircleX,
  Clock,
  CloudUpload,
  Coins,
  Contact,
  CreditCard,
  DollarSign,
  Download,
  Eye,
  FileText,
  Files,
  Filter,
  Globe,
  Hash,
  HeartPulse,
  History,
  Hourglass,
  House,
  IdCard,
  Image,
  Info,
  Key,
  Landmark,
  LayoutGrid,
  LogOut,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  Minus,
  Moon,
  MoonStar,
  MoreHorizontal,
  MoreVertical,
  Music,
  Palette,
  Pause,
  Pencil,
  Percent,
  Phone,
  Play,
  Plus,
  Receipt,
  RefreshCw,
  Search,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Star,
  Sticker,
  Sun,
  ToggleLeft,
  ToggleRight,
  Trash2,
  TrendingUp,
  TriangleAlert,
  Trophy,
  Upload,
  User,
  UserCheck,
  UserCog,
  UserPlus,
  UserX,
  Users,
  Wallet,
  X,
  Zap,
  ListOrdered,
} from 'lucide-angular';

// Map Bootstrap Icons class names (without 'bi-' prefix) to Lucide icon data.
// Used by <app-icon name="house-door"> to resolve the right SVG.
export const ICON_MAP: Record<string, any> = {
  // House / Home
  'house-door': House,
  'house-door-fill': House,

  // Bookmarks
  'bookmark-check': BookmarkCheck,
  'bookmark-check-fill': BookmarkCheck,

  // Calendar variants
  'calendar': Calendar,
  'calendar-fill': Calendar,
  'calendar3': Calendar,
  'calendar-week': CalendarDays,
  'calendar-week-fill': CalendarDays,
  'calendar-month': CalendarDays,
  'calendar-range': CalendarRange,
  'calendar-event': CalendarClock,
  'calendar-check': CalendarCheck,
  'calendar-day': CalendarDays,
  'calendar-plus': CalendarPlus,
  'calendar-plus-fill': CalendarPlus,
  'calendar-x': CalendarX,
  'calendar-star': CalendarHeart,

  // People / Users
  'person': User,
  'person-fill': User,
  'person-circle': CircleUser,
  'person-circle-fill': CircleUser,
  'person-check': UserCheck,
  'person-plus': UserPlus,
  'person-plus-fill': UserPlus,
  'person-x': UserX,
  'person-arms-up': User,
  'person-badge': IdCard,
  'person-exclamation': UserCog,
  'person-vcard': Contact,
  'people': Users,
  'people-fill': Users,

  // Buildings
  'building': Building,
  'building-fill': Building,
  'building-x': Building,

  // Navigation / UI
  'list': Menu,
  'list-ol': ListOrdered,

  // Theme / Appearance
  'moon': Moon,
  'moon-fill': Moon,
  'moon-stars': MoonStar,
  'three-dots': MoreHorizontal,
  'three-dots-vertical': MoreVertical,
  'sun': Sun,
  'sun-fill': Sun,
  'palette': Palette,

  // Settings / Refresh
  'gear': Settings,
  'gear-fill': Settings,
  'arrow-clockwise': RefreshCw,
  'arrow-repeat': RefreshCw,

  // Arrows
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  'arrow-up': ArrowUp,
  'arrow-left-right': ArrowLeftRight,

  // Misc awards / achievements
  'award': Award,
  'trophy': Trophy,

  // Finance / Money
  'bank': Landmark,
  'cash-coin': Coins,
  'cash-stack': Banknote,
  'credit-card': CreditCard,
  'credit-card-2-front': CreditCard,
  'currency-dollar': DollarSign,
  'currency-exchange': ArrowLeftRight,
  'wallet2': Wallet,
  'receipt': Receipt,
  'percent': Percent,

  // Charts
  'bar-chart': ChartBar,
  'graph-up': TrendingUp,

  // Communication
  'bell': Bell,
  'envelope': Mail,
  'telephone': Phone,
  'phone': Phone,
  'whatsapp': MessageCircle,

  // Media / Actions
  'bicycle': Bike,
  'heart-pulse': HeartPulse,
  'heartbeat': HeartPulse,

  // Auth / Security
  'box-arrow-right': LogOut,
  'shield-check': ShieldCheck,
  'shield-lock': ShieldAlert,
  'key': Key,

  // Camera / Media
  'camera': Camera,
  'image': Image,

  // Files / Documents
  'card-text': FileText,
  'file-text': FileText,
  'files': Files,
  'cloud-upload': CloudUpload,
  'download': Download,
  'upload': Upload,

  // Checks / Status
  'check': Check,
  'check-lg': Check,
  'check-circle': CircleCheck,
  'check-circle-fill': CircleCheck,

  // Chevrons
  'chevron-double-left': ChevronsLeft,
  'chevron-double-right': ChevronsRight,
  'chevron-down': ChevronDown,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'chevron-up': ChevronUp,

  // Shapes / Misc
  'circle': Circle,
  'dash': Minus,
  'hash': Hash,
  'plus': Plus,
  'plus-circle': CirclePlus,
  'x-lg': X,
  'x-circle': CircleX,
  'x-circle-fill': CircleX,
  'slash-circle': CircleSlash,

  // Time
  'clock': Clock,
  'clock-fill': Clock,
  'clock-history': History,
  'hourglass-split': Hourglass,

  // Map / Location
  'geo-alt': MapPin,

  // Globe
  'globe': Globe,
  'globe2': Globe,

  // Info / Alerts
  'info-circle': Info,
  'info-circle-fill': Info,
  'exclamation-circle': CircleAlert,
  'exclamation-circle-fill': CircleAlert,
  'exclamation-triangle': TriangleAlert,
  'exclamation-triangle-fill': TriangleAlert,

  // Eye
  'eye': Eye,

  // Filters / Layout
  'funnel': Filter,
  'columns-gap': LayoutGrid,

  // Media playback
  'play': Play,
  'play-circle': CirclePlay,
  'pause': Pause,
  'pause-circle': CirclePause,
  'pause-circle-fill': CirclePause,

  // Stars
  'star-fill': Star,

  // Sticky / Notes
  'sticky': Sticker,

  // Trash
  'trash': Trash2,

  // Toggle
  'toggle-off': ToggleLeft,
  'toggle-on': ToggleRight,

  // Search
  'search': Search,

  // Pencil / Edit
  'pencil': Pencil,

  // Lightning / Zap
  'lightning': Zap,
  'lightning-charge': Zap,

  // Music
  'music-note-beamed': Music,

  // Journal - mapped to BookOpen (libro abierto = "lista de clases programadas")
  'journal-bookmark': BookOpen,
  'journal-plus': BookOpen,

  // Peace - no Lucide equivalent; use CircleQuestionMark as placeholder
  // TODO: pick better icon for peace
  'peace': CircleQuestionMark,
};
